import { useState } from 'react';

import { Button } from '@capsule/consumer-design-system';

import orderBy from 'lodash/orderBy';
import { css } from 'styled-components';

import { ErrorText } from 'src/app/components/Elements/ErrorText/ErrorText';
import { useLoggedInTransferFormData } from 'src/app/hooks/feature_data/useLoggedInTransferFormData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { AccountPageAddDependent } from 'src/app/router/paths';
import type { PatientDetails } from 'src/app/types';
import { getFullName } from 'src/app/utils/patientUtils';

import type { RenderedStepComponentProps } from '..';
import Step from '../Step';

import { ButtonSection, FamilyButton, SelectFamilyButton } from './styles';

export const FamilyFormRenderer = ({ handleNextClick, patients }: RenderedStepComponentProps) => {
  useSegmentEvent('viewAddFamilyMemberConfirmation', { shouldTrackOnMount: true });
  const selectLoggedInTransferFormUserEvent = useSegmentEvent('selectLoggedInTransferFormUser', {
    shouldTrackOnMount: false,
  });

  const { loggedInTransferFormData, setLoggedInTransferFormData } = useLoggedInTransferFormData();

  const [transferPatientId, setTransferPatientId] = useState<number | null>(
    loggedInTransferFormData?.patientId ? parseInt(loggedInTransferFormData.patientId) : null
  );
  const [submitCount, setSubmitCount] = useState(0);

  const handleSubmit = async () => {
    setSubmitCount(prev => prev + 1);

    if (!transferPatientId) return;
    const patient = patients?.find(patient => patient.id.toString() === transferPatientId.toString());
    selectLoggedInTransferFormUserEvent({
      account_holder: patient?.isPrimary ? 'Primary' : 'Dependent',
    });

    await setLoggedInTransferFormData({
      ...loggedInTransferFormData,
      patientId: transferPatientId.toString(),
      patientName: patient?.firstName,
      patientIsPrimary: patient?.isPrimary,
      birthdate: patient?.birthdate,
    });

    handleNextClick();
  };

  const sortPatients = (allPatients: PatientDetails[]) =>
    orderBy(allPatients, [patient => patient?.isPrimary, patient => getFullName(patient)], ['desc', 'asc']);

  return (
    // Regular whitespace caused the sub-title to not render at all, which broke the layout. So U+200E it is.
    <Step title="Who is this medication for?" text="‎ ">
      {submitCount > 0 && !transferPatientId && (
        <ErrorText
          css={css`
            text-align: center;
            width: 100%;
            margin-bottom: ${({ theme }) => theme.space.s2};
          `}
        >
          You must make a selection before continuing.
        </ErrorText>
      )}

      <ButtonSection>
        {sortPatients(patients ?? []).map(patient => (
          <SelectFamilyButton
            onClick={() => setTransferPatientId(parseInt(patient.id))}
            size="medium"
            style="family"
            data-checked={parseInt(patient?.id) === transferPatientId}
            block
            type="button"
          >
            {getFullName(patient)}
          </SelectFamilyButton>
        ))}

        <FamilyButton to={AccountPageAddDependent.toPath()} size="medium" style="secondary" block>
          + Add A Family Member
        </FamilyButton>
      </ButtonSection>

      <Button
        onClick={handleSubmit}
        css={css`
          width: min-content;
          margin-top: ${({ theme }) => theme.space.s5};
          ${({ theme }) => theme.mediaQueries.smallDown} {
            width: 100%;
          }
        `}
        variant="contained-secondary-lg"
      >
        Continue
      </Button>
    </Step>
  );
};
